import { useToast } from "#/hooks/ToastContext";
import { qk } from "#/lib/queryKeys";
import {
  deleteArticleReview,
  updateArticleReview,
  UpdateArticleReviewRequest,
} from "#/services/articleReviews";
import { useMutation, useQueryClient } from "@tanstack/react-query";

type UpdatePayload = {
  id: string;
  payload: UpdateArticleReviewRequest;
};

type UseArticleReviewMutationsOptions = {
  articleId?: string;
};

export function useArticleReviewMutations(
  options?: UseArticleReviewMutationsOptions
) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const invalidate = () => {
    if (options?.articleId) {
      queryClient.invalidateQueries({
        queryKey: qk.articleReviews({ articleId: options.articleId }),
      });
    }
    queryClient.invalidateQueries({ queryKey: qk.articleReviewsArticles() });
    queryClient.invalidateQueries({ queryKey: qk.articleReviews() });
  };

  const updateReview = useMutation({
    mutationFn: ({ id, payload }: UpdatePayload) =>
      updateArticleReview(id, payload),
    onSuccess: () => {
      invalidate();
      showToast("Review updated.", "success");
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : "Failed to update review.";
      showToast(message, "error");
    },
  });

  const removeReview = useMutation({
    mutationFn: (id: string) => deleteArticleReview(id),
    onSuccess: () => {
      invalidate();
      showToast("Review deleted.", "success");
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : "Failed to delete review.";
      showToast(message, "error");
    },
  });

  return { updateReview, removeReview };
}

export default useArticleReviewMutations;
